/**
 * Implements a wallets watchlist with
 * Top Walellets section
 */
/* eslint-disable array-bracket-newline */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { enums } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import Loader from '../../../components/common/Loader';
import WatchlistRow from './WatchlistRow';
import WatchlistTabs from '../../../components/Watchlist/WatchlistTabs';
import {
  actionAddWatchlistWallet,
  actionRemoveWatchlistWallet,
  actionSetWalletWatchlistChain,
  actionSetWalletWatchlistTab,
  actionUpdateWatchlistWallet,
} from '../../../actions/wallet_watchlist';
import { IStore } from '../../../reducers';
import {
  IWatchedWallet,
  WalletWatchlistTab,
  WatchlistWallet,
} from '../../../reducers/wallet_watchlist';
import useSearchWallets from '../useSearchWalllets';
import { GetWalletFromWatchlistData } from '../../../selectors/watchlist';
import { UpdateWatchlistItemPayload } from '../../../reducers/watchlist';

import '../../../components/Watchlist/styles.scss';

const SEARCH_LIMIT = 6;

// as we don't have wallet_id for big trades wallets tab
// the only way to compare items is to use 'address-chain' unique key
const getKey = (address?: string, chain?: string) => `${address}-${chain}`;

// finds the same wallet in watchlist, otherwise returns undefined
const getWallet = (
  items: IWatchedWallet[],
  key: string,
  item: WatchlistWallet,
  tab: WalletWatchlistTab
): IWatchedWallet | undefined => {
  if (tab === WalletWatchlistTab.Star) {
    return item as IWatchedWallet;
  }
  const watchedItem = items.find(
    (wallet) => getKey(wallet.address, wallet.chain) === key
  );
  return watchedItem;
};

interface IWalletWatchlistProps {
  currentAddress?: string;
  chain?: string;
}

const Watchlist = ({ currentAddress, chain }: IWalletWatchlistProps) => {
  const watchedWallets = useSelector(
    (state: IStore) => state.wallet_watchlist.watchedWallets
  );
  const watchlistItems = useSelector(
    (state: IStore) => state.wallet_watchlist.wallets
  );
  const tab = useSelector((state: IStore) => state.wallet_watchlist.tab);
  const loading = useSelector(
    (state: IStore) => state.wallet_watchlist.loading
  );
  const walletTags = useSelector((state: IStore) => state.walletTags);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (chain) {
      dispatch(actionSetWalletWatchlistChain(chain));
    }
  }, [chain, dispatch]);

  const { searchedData } = useSearchWallets(search, SEARCH_LIMIT, chain);

  const collection = useMemo(
    () =>
      watchlistItems.filter((item) => {
        const tag = walletTags[`${item.chain}`]?.[`${item.address}`]?.[0];
        const searchStr = search.toLowerCase();
        return (
          item.address.toLowerCase().includes(searchStr) ||
          item.nickname.toLowerCase().includes(searchStr) ||
          tag?.toLowerCase().includes(searchStr)
        );
      }),
    [watchlistItems, search, walletTags]
  );

  const onSwitch = useCallback(
    (network: string, address: string) => {
      navigate(`/en/wallet-overview/${network}/${address}`);
    },
    [navigate]
  );

  const onRemove = useCallback(
    (id: number, callback: () => void) =>
      dispatch(actionRemoveWatchlistWallet(id, callback)),
    [dispatch]
  );

  const onAdd = useCallback(
    (
      data: GetWalletFromWatchlistData,
      tag?: enums.Tag,
      callback?: () => void
    ) => dispatch(actionAddWatchlistWallet(data, tag, callback)),
    [dispatch]
  );

  const onUpdate = useCallback(
    (id: number, payload: UpdateWatchlistItemPayload) => {
      dispatch(actionUpdateWatchlistWallet(id, payload));
    },
    [dispatch]
  );

  const handleOnDragEnd = useCallback<OnDragEndResponder>(
    ({ source, destination }) => {
      if (!destination || source.index === destination.index) {
        return;
      }
      const draggedItem = collection[source.index];

      if ('wallet_id' in draggedItem) {
        onUpdate(draggedItem.wallet_id, {
          old: source.index,
          new: destination.index,
        });
      }
    },
    [collection, onUpdate]
  );

  const topThreeTickets = useMemo(() => {
    const items = [...watchlistItems, ...watchedWallets];
    const excludes: any = items.reduce(
      (cons, el) => ({ ...cons, [getKey(el.address, el.chain)]: el.address }),
      {}
    );

    return searchedData
      ?.filter(
        (item) =>
          !excludes[getKey(item.address, item.chain)] &&
          getKey(item.address, item.chain) !== getKey(currentAddress, chain)
      )
      .slice(0, 3);
  }, [watchlistItems, watchedWallets, currentAddress, chain, searchedData]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className='centrify'>
          <Loader />
        </div>
      );
    }
    return (
      <>
        {collection.map((item, index) => {
          const currentKey = getKey(currentAddress, chain);
          const itemKey = getKey(item.address, item.chain);
          const watchedItem = getWallet(watchedWallets, itemKey, item, tab);
          return (
            <Draggable
              key={itemKey}
              draggableId={itemKey.toString()}
              index={index}
              isDragDisabled={tab !== WalletWatchlistTab.Star}
            >
              {(provided) => (
                <div
                  className='watchlist__draggable'
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  <WatchlistRow
                    actionSwitchCurrent={
                      currentKey === itemKey ? undefined : onSwitch
                    }
                    actionRemoveWatchlist={watchedItem ? onRemove : undefined}
                    actionAddWatchlist={watchedItem ? undefined : onAdd}
                    actionUpdateWatchlist={watchedItem ? onUpdate : undefined}
                    item={item}
                    active={currentKey === itemKey}
                    id={watchedItem?.wallet_id}
                  />
                </div>
              )}
            </Draggable>
          );
        })}
      </>
    );
  };

  return (
    <div className='watchlist'>
      <div className='container'>
        <WatchlistTabs
          tabs={[
            WalletWatchlistTab.TopWallets,
            WalletWatchlistTab.BigTradesWallets,
            WalletWatchlistTab.ActiveWallets,
          ]}
          starTab={WalletWatchlistTab.Star}
          activeTab={tab}
          setTab={(tab: WalletWatchlistTab) =>
            dispatch(actionSetWalletWatchlistTab(tab))
          }
        />
        <input
          type='search'
          className='search__input text--12 text--grey-600 text--weight-600'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search in Watchlist...'
        />
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId='wallet-watchlist-items'>
            {(provided, snapshot) => (
              <div
                className={`items_container ${
                  snapshot.isDraggingOver ? 'items_container__overlay' : ''
                }`}
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {renderContent()}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {!!topThreeTickets?.length && (
          <div>
            <div className='watchlist__search_divider'>
              <Text
                size={14}
                className='watchlist__search_divider__search text--grey-400'
              >
                search
              </Text>
            </div>
            {topThreeTickets.map((item) => (
              <WatchlistRow
                key={`${item.wallet_id}-${item.chain}-${item.balance_usd}`}
                actionSwitchCurrent={onSwitch}
                actionAddWatchlist={onAdd}
                item={item}
                active={false}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Watchlist;
